import * as variablesDefault from './skinning/skinning_variables_default.json';
import * as variablesDarkGs from './skinning/skinning_variables_dark_gs.json';
import * as variablesDarkWhitelabel from './skinning/skinning_variables_dark_whitelabel.json';
import * as variablesLightWhitelabel from './skinning/skinning_variables_light_whitelabel.json';
import * as variablesLightHighContrast from './skinning/skinning_variables_high_contrast.json';
import { SkinConfig, skinConfigProperties, SkinConfigValue, SkinConfigValueIf, SkinConfigValueSassFunction } from './skinning/skinconfig.model';
import * as _ from 'lodash';
import tinycolor from 'tinycolor2';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

export type ColorScheme = 'light' | 'dark' | 'browser';

/**
 * this is the real skinning service logic, no angular specific stuff, no nestJS specific stuff
 * this library is extended from ingrid/services/skinning.service (Angular) and maja/skin/skinning.service
 */
export class SkinningLib {
  gsDarkModeColor = '#82b5c6';
  colorScheme$ = new BehaviorSubject<ColorScheme>('browser');
  #browserColorScheme$ = new BehaviorSubject<'light' | 'dark'>('light');
  highContrastMode$ = new BehaviorSubject(false);

  darkMode$ = combineLatest([
    this.colorScheme$,
    this.#browserColorScheme$
  ]).pipe(
    map(([colorScheme, browserColorScheme]) => {
      return colorScheme === 'dark' || colorScheme === 'browser' && browserColorScheme === 'dark';
    })
  );

  overrideProperties$ = new BehaviorSubject<SkinConfig>({}); // needed for fixed logo PaPa


  stylePropertiesBranding$ = new BehaviorSubject<SkinConfig>({}); // will be set from configService if there is a branding config
  stylePropertiesDefault = this.#processJsonVariables(variablesDefault);
  stylePropertiesDarkmodeWhitelabel = this.#processJsonVariables(variablesDarkWhitelabel);
  stylePropertiesDarkmodeGs = this.#processJsonVariables(variablesDarkGs);
  stylePropertiesHighContrast = this.#processJsonVariables(variablesLightHighContrast);


  isGreylabel$ = combineLatest([
    this.darkMode$,
    this.stylePropertiesBranding$
  ]).pipe(
    map(([darkMode, stylePropertiesBranding]) => {
      if (_.isEmpty(stylePropertiesBranding)) {
        return true;
      }
      // if the primary- and secondary colors have default values it's definitely greylabel
      if (stylePropertiesBranding.primary_color === '#15425f' && stylePropertiesBranding.secondary_color === '#2e83a0') {
        return true;
      }
      // if we are in darkmode and the gridscale default darkmode color is set we behave like greylabel (but if it is just *not* set and we are no greylabel because of the `if` above we use the whitelabel darkmode which will use the primary color as darkmode color)
      if (darkMode && (stylePropertiesBranding.dark_mode_color === '#82b5c6'/* || stylePropertiesBranding.dark_mode_color === null || stylePropertiesBranding.dark_mode_color === undefined*/)) {
        return true;
      }



      return false;
    })
  )

  stylePropertiesLightmode$ = this.isGreylabel$.pipe(
    map(isGreylabel => {
      if (!isGreylabel) {
        return variablesLightWhitelabel;
      }
      return {};
    })
  );

  stylePropertiesDarkmode$ = combineLatest([
    this.isGreylabel$,
    this.stylePropertiesBranding$
  ]).pipe(
    map(([isGreylabel, stylePropertiesBranding]) => {
      const stylePropertiesDefault = this.stylePropertiesDefault;
      const stylePropertiesDarkmodeWhitelabel = this.stylePropertiesDarkmodeWhitelabel;
      return isGreylabel
        ? this.stylePropertiesDarkmodeGs
        : this.setupDarkmodeWhitelabelForBranding(stylePropertiesDefault, stylePropertiesDarkmodeWhitelabel, stylePropertiesBranding)
    })
  );



  // merge all values together but don't resolve - this data is then used for resolving later
  #stylePropertiesMerged$ = combineLatest([
    this.darkMode$,
    this.stylePropertiesBranding$,
    this.stylePropertiesDarkmode$,
    this.stylePropertiesLightmode$,
    this.highContrastMode$
  ]).pipe(
    map(([darkMode, stylePropertiesBranding, stylePropertiesDarkmode, stylePropertiesLightmode, highContrast]) => (({
      ...this.stylePropertiesDefault,
      ...stylePropertiesBranding,
      logo_data: darkMode && stylePropertiesBranding?.logo_dark_data ? stylePropertiesBranding?.logo_dark_data : stylePropertiesBranding?.logo_data || 'transparent',
      logo_width: darkMode && stylePropertiesBranding?.logo_dark_width ? stylePropertiesBranding?.logo_dark_width : stylePropertiesBranding?.logo_width,
      ...(darkMode ? stylePropertiesDarkmode : stylePropertiesLightmode),
      ...(highContrast ? this.stylePropertiesHighContrast : {})
    }))),
  );
  #stylePropertiesMerged: SkinConfig = {};

  /**
   * merged and resolved style properties
   * (nearly the same computation is made in the designer.service in PaPa for the preview)
   */
  styleProperties$ = combineLatest([
    this.darkMode$,
    this.stylePropertiesBranding$,
    this.stylePropertiesDarkmode$,
    this.stylePropertiesLightmode$,
    this.overrideProperties$,
    this.#stylePropertiesMerged$,
    this.highContrastMode$
  ]).pipe(
    map(([darkMode, stylePropertiesBranding, stylePropertiesDarkmode, stylePropertiesLightmode, overrideProperties, stylePropertiesMerged, highContrast]) => _.mapValues({
      ...this.stylePropertiesDefault,
      ...stylePropertiesBranding,
      logo_data: darkMode && stylePropertiesBranding?.logo_dark_data ? stylePropertiesBranding?.logo_dark_data : stylePropertiesBranding?.logo_data || 'transparent',
      logo_width: darkMode && stylePropertiesBranding?.logo_dark_width ? stylePropertiesBranding?.logo_dark_width : stylePropertiesBranding?.logo_width,
      ...(darkMode ? stylePropertiesDarkmode : stylePropertiesLightmode),
      ...(highContrast ? this.stylePropertiesHighContrast : {}),
      ...overrideProperties
    }, v => this.resolveValue(v || '', stylePropertiesMerged)))
  );

  primaryColorHsl$ = this.styleProperties$.pipe(
    map(styleProperties => tinycolor(styleProperties.primary_color).toHsl())
  );
  darkModeColorHsl$ = this.styleProperties$.pipe(
    map(styleProperties => tinycolor(styleProperties.dark_mode_color).toHsl())
  );

  stylePropertyTargetElement$ = new BehaviorSubject<HTMLElement | undefined>(undefined);



  constructor() {
    combineLatest([
      this.stylePropertyTargetElement$,
      this.styleProperties$
    ]).subscribe(([stylePropertyTargetElement, styleProperties]) => {
      // this effect set the style properties to the document DOM when properties change!
      if (stylePropertyTargetElement) {
        this.setStyleProperties(styleProperties, stylePropertyTargetElement);
      }

    });

    if (typeof (document) !== 'undefined') {
      this.darkMode$.subscribe(darkmode => {
        if (darkmode) {
          document.body.classList.add('darkmode');
        } else {
          document.body.classList.remove('darkmode');
        }
      });

      this.highContrastMode$.subscribe(hc => {
        if (hc) {
          document.body.classList.add('highcontrast');
        } else {
          document.body.classList.remove('highcontrast');
        }
      })
    }

    this.#stylePropertiesMerged$.subscribe(stylePropertiesMerged => {
      this.#stylePropertiesMerged = stylePropertiesMerged;
    });


    if (typeof (window) !== 'undefined' && window.matchMedia) {
      if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
        this.#browserColorScheme$.next('dark');
      }

      window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', event => {
        this.#browserColorScheme$.next(event.matches ? "dark" : "light");
      });


      if (window.matchMedia('(forced-colors: active)').matches) {
        this.highContrastMode$.next(true);
      }

      window.matchMedia('(forced-colors: active)').addEventListener('change', event => {
        this.highContrastMode$.next(event.matches);
      });

    }
  }


  /**
   * returns the default style properties (without branding and darkmode colors)
   */
  getStylePropertiesDefault(noFinallyResolveFor?: string[]) {
    return _.mapValues(
      this.stylePropertiesDefault,
      (v, k) => this.resolveValue(v || '', this.#stylePropertiesMerged, noFinallyResolveFor?.indexOf(k) !== -1)
    );
  }

  /**
   * fix the crap the API delivers
   */
  parseApiVariables(variables: SkinConfig): SkinConfig {
    const vars: SkinConfig = {};
    _.forEach(variables, (value: string | boolean, varName: string) => {
      if (skinConfigProperties.indexOf(varName) !== -1 && value !== undefined && value !== null && value !== 'undefined' && value !== 'null' && value !== '' && value !== 'null') {
        if ((varName === 'logo_data' || varName === 'logo_dark_data') && typeof value === 'string' && (value.match(/^(https?:)?\//) || value.match(/^data:/))) {
          value = 'url(' + value + ')';
        }

        if (varName === 'logo_width' || varName === 'logo_dark_width') {
          value += 'px';
        }

        if (varName === 'background_data' && (value as string).match(/[a-z]+:/)) {
          value = 'url(' + value + ')';
        }

        // there is a typo in the backend
        if (varName === 'buttons_color' && variables.button_color === undefined) {
          varName = 'button_color';
        }

        if (varName === 'dark_mode_color_intensity') {
          const valInt = typeof (value) === 'number' ? value : typeof (value) === 'string' ? parseInt(value) : 80;
          const intensityReverse = 100 - valInt;
          value = String(intensityReverse * 0.7 + 30);
        }
        _.set(vars, [varName], String(value));
      }
    });

    return vars;
  }

  /**
   * set the branding variables from the config endpoint
   */
  setBrandingVariables(variables: SkinConfig) {
    this.stylePropertiesBranding$.next(this.parseApiVariables(variables));
  }

  setStyleProperties(styleProperties: Record<string, string>, targetElement: HTMLElement) {
    // cleanup first
    let stylePropertiesSet: string[] = [];
    if (targetElement.hasAttribute('ig-style-properties-set')) {
      const propString = targetElement.getAttribute('ig-style-properties-set');
      if (typeof propString === 'string') {
        try {
          stylePropertiesSet = JSON.parse(propString);
        } catch {
          // we don't care
        }
      }
    }

    _.forEach(stylePropertiesSet, dashedPropName => {
      targetElement.style.removeProperty(dashedPropName);
    });
    stylePropertiesSet = [];

    _.forEach(styleProperties, (propVal, propName) => {
      if (propName.indexOf('_') === 0) {
        // private - do not export
        return true;
      }
      if (propVal !== undefined) {
        const dashedPropName = "--" + this.getDashedVarName(propName);
        stylePropertiesSet.push(dashedPropName);
        targetElement.style.setProperty(dashedPropName, String(propVal));
      }
    });

    targetElement.setAttribute('ig-style-properties-set', JSON.stringify(stylePropertiesSet));
  }

  getDashedVarName(varName: string) {
    // nameDashed function copied from the `export_types.js` script used for generating the ...props.js file during ingrid build
    return varName
      .replace("$", "")
      .replace(/([A-Z])/g, _match => {
        if (typeof _match === "string") {
          return "-" + _match.toLowerCase();
        }
        return _match;
      })
      .replace(/_/g, '-');
  }

  #getUnderscoredVarName(varName: string) {
    return varName
      .replace("$", "")
      .replace(/([A-Z])/g, _match => {
        if (typeof _match === "string") {
          return "_" + _match.toLowerCase();
        }
        return _match;
      })
      .replace(/-/g, '_');
  }

  /**
   * resolve a variable value/function etc. to the plain value
   * @param varValue the value
   * @param stopAtPlainValue true if resolving should be stopped at first plain value (no function)
   * @returns
   */
  resolveValue(varValue: SkinConfigValue, allStylePropertiesUnresolved: SkinConfig, doNotResolveSkinVariable = false, resolveChain: string[] = []): string {
    // check if the varValue is a variable itself and resolve
    const igMatches = typeof varValue === 'string' ? varValue.match(/^\$(ig[A-Z][a-zA-Z0-9_]+)$/) : false;
    const variableMatches = typeof varValue === 'string' ? varValue.match(/^\$([a-zA-Z0-9_]+)$/) : false;
    if (typeof (varValue) === 'number') {
      return String(varValue);

    } else if (igMatches) {
      const keyUnderscored = this.#getUnderscoredVarName(igMatches[1]);

      if (keyUnderscored in allStylePropertiesUnresolved) {
        if (resolveChain.indexOf(keyUnderscored) >= 0) {
          console.warn('skinning.service resolveValue recursion protection: Resolving', resolveChain.join('->') + '->' + keyUnderscored);
          return String(varValue);
        }
        resolveChain.push(keyUnderscored);
        return this.resolveValue(_.get(allStylePropertiesUnresolved, keyUnderscored), allStylePropertiesUnresolved, false, resolveChain);
      }

    } else if (variableMatches && !doNotResolveSkinVariable) {
      const keyUnderscored = this.#getUnderscoredVarName(variableMatches[1]);

      if (keyUnderscored in allStylePropertiesUnresolved) {
        if (resolveChain.indexOf(keyUnderscored) >= 0) {
          console.warn('skinning.service resolveValue recursion protection: Resolving', resolveChain.join('->') + '->' + keyUnderscored);
          return String(varValue);
        }
        resolveChain.push(keyUnderscored);
        return this.resolveValue(_.get(allStylePropertiesUnresolved, keyUnderscored), allStylePropertiesUnresolved, false, resolveChain);
      }

    } else if (typeof varValue === 'object' && (varValue as SkinConfigValueSassFunction)['sass-function'] !== undefined) {
      varValue = varValue as SkinConfigValueSassFunction
      // uha we need to execute a sass function
      // rgba
      if (varValue['sass-function'] === 'rgba') {
        const inputColor = this.resolveValue(varValue['sass-function-args'][0], allStylePropertiesUnresolved);
        if (inputColor) {
          const match = (inputColor as string).match(/^#([a-f0-9]){3,6}$/i);
          if (match) {
            const rgb = this.hexToRGB(inputColor);
            let output = 'rgba(' + rgb.r + ', ' + rgb.g + ', ' + rgb.b;
            if (varValue['sass-function-args'][1]) {
              output += ', ' + varValue['sass-function-args'][1];
            }
            output += ')';
            return output;

          } else {
            console?.warn('skinning.service implementation of sass-rgba only supports hexadecimal values as parameter 0', varValue);
          }
        }

      } else if (varValue['sass-function'] === 'color.scale') {
        // color.scale
        const inputColor = this.resolveValue(varValue['sass-function-args'][0], allStylePropertiesUnresolved);
        // we need HSL notation first
        const hsl = this.hexToHSL(inputColor);
        if (hsl) {
          varValue['sass-function-args'].slice(1).forEach(param => {
            if (typeof param === 'string') {
              const match = param.match(/^\$(lightness|saturation):\s?(-?[0-9]+)%?$/);
              if (match) {
                const key = match[1];
                const scale = parseInt(match[2], 10);

                switch (key) {
                  case 'lightness':
                    hsl.l = Math.round((hsl.l + (scale > 0 ? 100 - hsl.l : hsl.l) * (scale / 100)) * 100) / 100;
                    break;

                  case 'saturation':
                    hsl.s = Math.round((hsl.s + (scale > 0 ? 100 - hsl.s : hsl.s) * (scale / 100)) * 100) / 100;
                    break;
                }

              } else {
                console?.warn('skinning.service: unsupported parameter for sass function "color.scale"', param, 'only lightnessor saturation is supported');
              }

            } else {
              console?.warn('skinning.service: unsupported parameter for sass function "color.scale". Must be of type string', param);
            }

          });


          // and back to hex and set
          return this.HSLToHex(hsl.h, hsl.s, hsl.l);
        }


      } else if (varValue['sass-function'] === 'adjust-hsl') {
        const inputColor = this.resolveValue(varValue['sass-function-args'][0], allStylePropertiesUnresolved);
        const hsl = tinycolor(inputColor).toHsl();
        if (hsl) {
          varValue['sass-function-args'].slice(1).forEach(param => {
            if (typeof param === 'string') {
              const match = param.match(/^\$(lightness|saturation|hue):\s?(-?[0-9]+)$/);
              if (match) {
                const key = match[1];
                const value = parseInt(match[2], 10);

                switch (key) {
                  case 'lightness':
                    hsl.l = value;
                    break;

                  case 'saturation':
                    hsl.s = value;
                    break;

                  case 'hue':
                    hsl.h = value;
                    break;
                }

              } else {
                console?.warn('skinning.service: unsupported parameter for sass function "adjust-hsl"', param, 'only lightnessor saturation is supported');
              }
            } else {
              console?.warn('skinning.service: unsupported parameter for sass function "adjust-hsl". Must be of type string', param);
            }
          });

          return '#' + tinycolor(hsl).toHex();
        }

      } else if (varValue['sass-function'] === 'darken') {
        const inputColor = this.resolveValue(varValue['sass-function-args'][0], allStylePropertiesUnresolved);
        let blackFallbackColor: string | undefined = undefined;

        if (varValue['sass-function-args'].length === 3 && typeof varValue['sass-function-args'][2] === 'string') {
          // if a third parameter is set, this is a fallback color used in case the value gets black
          blackFallbackColor = varValue['sass-function-args'][2];
        }

        let darkenValue;
        if (typeof varValue['sass-function-args'][1] === 'number') {
          darkenValue = varValue['sass-function-args'][1];
        } else {
          darkenValue = parseFloat(this.resolveValue(varValue['sass-function-args'][1], allStylePropertiesUnresolved));
        }

        if (isNaN(darkenValue)) {
          console.warn('Could not resolve numeric value for darken function', varValue['sass-function-args'][1]);
          return '#FF0000';
        }

        let darkenedColor = '#' + tinycolor(inputColor).darken(darkenValue).toHex();

        if (darkenedColor === '#000000' && blackFallbackColor) {
          darkenedColor = blackFallbackColor;
        }

        return darkenedColor;

      } else if (varValue['sass-function'] === 'lighten') {
        const inputColor = this.resolveValue(varValue['sass-function-args'][0], allStylePropertiesUnresolved);

        let lightenValue;
        if (typeof varValue['sass-function-args'][1] === 'number') {
          lightenValue = varValue['sass-function-args'][1];
        } else {
          lightenValue = parseFloat(this.resolveValue(varValue['sass-function-args'][1], allStylePropertiesUnresolved));
        }

        if (isNaN(lightenValue)) {
          console.warn('Could not resolve numeric value for lighten function', varValue['sass-function-args'][1]);
          return '#FF0000';
        }

        return '#' + tinycolor(inputColor).lighten(lightenValue).toHex();

      } else {
        console?.warn('skinning.service sass function ', varValue['sass-function'], ' not implemented');
      }

    } else if (typeof varValue === 'object' && (varValue as SkinConfigValueIf)['if'] !== undefined && (varValue as SkinConfigValueIf)['then'] !== undefined && (varValue as SkinConfigValueIf)['else'] !== undefined) {
      varValue = varValue as SkinConfigValueIf;
      // condition
      const ifVar = varValue['if'][0];
      const ifVal = this.resolveValue(ifVar, allStylePropertiesUnresolved);



      if (ifVal === varValue['if'][1]) {
        return this.resolveValue(varValue['then'], allStylePropertiesUnresolved);
      } else {
        return this.resolveValue(varValue['else'], allStylePropertiesUnresolved);
      }
    }

    // if nothing handled the value before it's maybe a simple string that can be retrned
    return typeof varValue === 'string' ? varValue : '';
  }

  /**
   * converts a hex color notation to rgb values
   */
  hexToRGB(hex: string) {
    if (hex.indexOf('#') === 0) {
      hex = hex.substring(1);
    }
    const r = parseInt(hex.length === 3 ? hex[0].repeat(2) : hex[0] + hex[1], 16);
    const g = parseInt(hex.length === 3 ? hex[1].repeat(2) : hex[2] + hex[3], 16);
    const b = parseInt(hex.length === 3 ? hex[2].repeat(2) : hex[4] + hex[5], 16);

    return {
      r, g, b
    };
  }

  /**
   * converts a hex color notation to HSL notation
   * based on https://css-tricks.com/converting-color-spaces-in-javascript/
   */
  hexToHSL(hex: string) {
    // Convert hex to RGB first
    const rgb = this.hexToRGB(hex);

    // Then to HSL
    const r = rgb.r / 255;
    const g = rgb.g / 255;
    const b = rgb.b / 255;
    const cmin = Math.min(r, g, b),
      cmax = Math.max(r, g, b),
      delta = cmax - cmin;
    let h = 0,
      s = 0,
      l = 0;

    if (delta == 0)
      h = 0;
    else if (cmax == r)
      h = ((g - b) / delta) % 6;
    else if (cmax == g)
      h = (b - r) / delta + 2;
    else
      h = (r - g) / delta + 4;

    h = Math.round(h * 60);

    if (h < 0)
      h += 360;

    l = (cmax + cmin) / 2;
    s = delta == 0 ? 0 : delta / (1 - Math.abs(2 * l - 1));
    s = +(s * 100).toFixed(1);
    l = +(l * 100).toFixed(1);

    return {
      h, s, l
    };
  }

  /**
   * converts HSL color notation to hex notation
   * found on https://css-tricks.com/converting-color-spaces-in-javascript/
   */
  HSLToHex(h: number, s: number, l: number) {
    s /= 100;
    l /= 100;

    const c = (1 - Math.abs(2 * l - 1)) * s,
      x = c * (1 - Math.abs((h / 60) % 2 - 1)),
      m = l - c / 2;
    let r = 0,
      g = 0,
      b = 0;

    if (0 <= h && h < 60) {
      r = c; g = x; b = 0;
    } else if (60 <= h && h < 120) {
      r = x; g = c; b = 0;
    } else if (120 <= h && h < 180) {
      r = 0; g = c; b = x;
    } else if (180 <= h && h < 240) {
      r = 0; g = x; b = c;
    } else if (240 <= h && h < 300) {
      r = x; g = 0; b = c;
    } else if (300 <= h && h < 360) {
      r = c; g = 0; b = x;
    }
    // Having obtained RGB, convert channels to hex
    let r2 = Math.round((r + m) * 255).toString(16);
    let g2 = Math.round((g + m) * 255).toString(16);
    let b2 = Math.round((b + m) * 255).toString(16);

    // Prepend 0s, if necessary
    if (r2.length == 1)
      r2 = "0" + r2;
    if (g2.length == 1)
      g2 = "0" + g2;
    if (b2.length == 1)
      b2 = "0" + b2;

    return "#" + r2 + g2 + b2;
  }


  #processJsonVariables(variables: Record<string, unknown>) {
    const skinConfig: SkinConfig = {};

    _.forEach(variables, (defaultValue, varName) => {
      if (varName === '$schema' || varName.indexOf('###') === 0 || varName === 'default') {
        // comment - skip
        return true;
      }
      const apiName = varName.replace(/(.)([A-Z])/g, (x, y, z) => y + '_' + z.toLowerCase()); // convert camelCase to snake_case

      if (skinConfigProperties.indexOf(apiName) >= 0) {
        _.set(skinConfig, [apiName], defaultValue);
      } else {
        console.warn('Unknown skinning property, defined in JSON but not in model. Skipping', apiName);
      }
    });

    return skinConfig;
  }


  setupDarkmodeWhitelabelForBranding(stylePropertiesDefault: SkinConfig, stylePropertiesDarkWhitelabel: SkinConfig, stylePropertiesBranding: SkinConfig) {
    return {
      ...stylePropertiesDarkWhitelabel,
      // we explicitely set the primary_color here cause setting it in the json leads to recursion
      primary_color: stylePropertiesBranding?.dark_mode_color || stylePropertiesBranding?.primary_color || stylePropertiesDefault.primary_color,
      // if the background (for auth) is a color we set the viewpport background so we dont have a too bright color
      background_data: stylePropertiesBranding?.background_data?.toString().indexOf('url(') !== 0 ? stylePropertiesDarkWhitelabel.ig_color_viewport_background : stylePropertiesBranding?.background_data
    }
  }

}
